import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  GrantScopeRequest,
  ScopeCapabilitiesResponse,
  SetScopeCapabilityRequest,
  TeamMember,
} from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'

export const teamQuery = (condoId: number) =>
  queryOptions({
    queryKey: queryKeys.team(condoId),
    queryFn: ({ signal }) =>
      api
        .get<TeamMember[]>('/condominiums/:id/scopes', { params: { id: condoId }, signal })
        .then((d) => d ?? []),
  })

export const scopeCapabilitiesQuery = (condoId: number) =>
  queryOptions({
    queryKey: queryKeys.scopeCapabilities(condoId),
    queryFn: ({ signal }) =>
      api.get<ScopeCapabilitiesResponse>('/condominiums/:id/scope-capabilities', {
        params: { id: condoId },
        signal,
      }),
  })

export function useGrantScope(condoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: GrantScopeRequest) =>
      api.post<TeamMember>('/condominiums/:id/scopes', body, { params: { id: condoId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.team(condoId) }),
  })
}

export function useRevokeScope(condoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (scopeId: number) =>
      api.delete('/condominiums/:id/scopes/:scopeId', { params: { id: condoId, scopeId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.team(condoId) }),
  })
}

/** The API sets one override per call; the screen sends one PUT per toggle that changed. */
export function useSaveScopeCapabilities(condoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (changes: SetScopeCapabilityRequest[]) => {
      let latest: ScopeCapabilitiesResponse | undefined
      for (const change of changes) {
        latest = await api.put<ScopeCapabilitiesResponse>(
          '/condominiums/:id/scope-capabilities',
          change,
          { params: { id: condoId } },
        )
      }
      return latest
    },
    onSuccess: (latest) => {
      if (latest) qc.setQueryData(queryKeys.scopeCapabilities(condoId), latest)
      // Capabilities of everyone on the team changed: re-gate the nav.
      void qc.invalidateQueries({ queryKey: queryKeys.memberships })
    },
  })
}
